import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/app/Bits";
import { useDogs } from "@/context/DogsContext";
import { Plus, Baby, ChevronRight, Trash2, Home, Send, Sparkles, Mail, DollarSign } from "lucide-react";
import { toast } from "sonner";

function StatTile({ label, value, icon: Icon, accent, sub }) {
  return (
    <div className="flex items-start gap-3 p-4 border border-border rounded-lg bg-card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`shrink-0 h-9 w-9 rounded-md flex items-center justify-center ${accent || "bg-secondary text-secondary-foreground"}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-display font-bold tracking-tight leading-none truncate">{value}</p>
        <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
        {sub ? <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p> : null}
      </div>
    </div>
  );
}

// Lightweight USD formatter so $0 doesn't show as "$0.00" (too noisy in a tile).
function fmtMoney(n) {
  const v = Number(n || 0);
  if (v === 0) return "$0";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function YearRecapButton() {
  // The recap defaults to last year in Jan, current year-to-date otherwise.
  // Keep this UI gentle — it's a one-time-per-year nicety.
  const [busy, setBusy] = useState(false);
  const sendRecap = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/breeder/year-summary/send");
      if (data?.ok) {
        toast.success(`Year recap sent for ${data.summary.year} — check your inbox.`);
      } else {
        toast.message("Recap built — email skipped (Resend not configured or you've opted out).");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send recap");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 p-4 border border-dashed border-border rounded-lg bg-secondary/30" data-testid="year-recap-card">
      <div className="min-w-0">
        <p className="text-sm font-medium">Year-in-review email</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Get a recap of your litters, puppies tracked, and homes placed. Auto-sent in January &mdash; or grab yours anytime.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={sendRecap} disabled={busy} className="shrink-0" data-testid="year-recap-send">
        <Mail size={14} className="mr-1.5" /> {busy ? "Sending…" : "Email me my recap"}
      </Button>
    </div>
  );
}

export default function LittersPage() {
  const { dogs } = useDogs();
  const [litters, setLitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ dam_dog_id: "", dam_name: "", stud_name: "", backup_stud_name: "", stud_fee: "", bred_on: "", is_planned: false });

  const load = async () => {
    setLoading(true);
    try {
      const [litRes, statsRes] = await Promise.all([
        api.get("/litters"),
        api.get("/breeder/stats").catch(() => ({ data: null })),
      ]);
      setLitters(litRes.data.items || []);
      if (statsRes?.data) setStats(statsRes.data);
    } catch (err) {
      toast.error("Could not load litters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    // Enforce dam ↔ dog profile linkage for new litters when the user has
    // any dogs on file. Editing legacy name-only records is out of scope for
    // this form (that lives on the detail page).
    if ((dogs || []).length > 0 && !form.dam_dog_id) {
      toast.error("Pick which of your dogs is the dam");
      return;
    }
    if (!form.dam_dog_id && !form.dam_name) {
      toast.error("Dam name or linked dog is required");
      return;
    }
    setCreating(true);
    try {
      // Coerce stud_fee to a float; backend expects a number, leave empty as 0.
      // Auto-fill dam_name from the linked dog so the snapshot on the litter
      // survives future dog renames.
      const payload = { ...form, stud_fee: form.stud_fee === "" ? 0 : parseFloat(form.stud_fee) || 0 };
      if (payload.dam_dog_id && !payload.dam_name) {
        const d = (dogs || []).find((x) => x.id === payload.dam_dog_id);
        if (d) payload.dam_name = d.name;
      }
      const { data } = await api.post("/litters", payload);
      toast.success(form.is_planned ? "Planned litter saved" : "Litter created");
      setForm({ dam_dog_id: "", dam_name: "", stud_name: "", backup_stud_name: "", stud_fee: "", bred_on: "", is_planned: false });
      // Refresh both the litter list AND the stats tiles so stud-fee total
      // updates immediately.
      load();
      // Optimistic add isn't needed since load() will repopulate, but we
      // keep it for instant visual feedback while the round-trip is in flight.
      setLitters((prev) => [{ ...data, puppy_count: 0 }, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create litter");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete litter "${label}"? This removes all puppies, weights, ENS and socialization data.`)) return;
    try {
      await api.delete(`/litters/${id}`);
      setLitters((prev) => prev.filter((l) => l.id !== id));
      toast.success("Litter deleted");
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const promote = async (id, label) => {
    if (!window.confirm(`Promote "${label}" from a planned litter to an actual litter?`)) return;
    try {
      const { data } = await api.post(`/litters/${id}/promote`);
      setLitters((prev) => prev.map((l) => (l.id === id ? { ...l, ...data, is_planned: false } : l)));
      toast.success("Litter promoted — puppies, weights & ENS unlocked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not promote");
    }
  };

  return (
    <div className="space-y-8" data-testid="litters-page">
      <SectionTitle overline="Breeder" title="Litters" />

      {/* Lifetime tally — visible only after first fetch so empty-state isn't noisy */}
      {stats && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="breeder-stats">
            <StatTile label="Litters" value={stats.litters_total} icon={Baby} accent="bg-primary/10 text-primary" />
            <StatTile label="Puppies tracked" value={stats.puppies_total} icon={Sparkles} accent="bg-secondary text-secondary-foreground" />
            <StatTile label="Sent to new homes" value={stats.puppies_placed} icon={Home} accent="bg-primary/10 text-primary" />
            <StatTile label="Invites pending" value={stats.invites_pending} icon={Send} accent="bg-secondary text-secondary-foreground" />
            <StatTile
              label="Stud fees"
              value={fmtMoney(stats.stud_fees_lifetime)}
              icon={DollarSign}
              accent="bg-primary/10 text-primary"
              sub={stats.stud_fees_year ? `${fmtMoney(stats.stud_fees_year)} year-to-date` : "lifetime"}
            />
          </div>
          <YearRecapButton />
        </>
      )}

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="font-medium">Start or plan a new litter</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Actual litters are ready to log puppies, weights, ENS and socialization. Planned litters are a private sketch — promote them later when the breeding actually happens.
              </p>
            </div>
            {/* Segmented mode picker — same shape/height as the input inputs
                below so it doesn't fight the form. */}
            <div className="inline-flex rounded-md border border-border bg-secondary/30 p-0.5 shrink-0" role="radiogroup" data-testid="litter-mode-toggle">
              <button
                type="button"
                onClick={() => setForm({ ...form, is_planned: false })}
                className={`px-3 py-1.5 text-xs font-semibold rounded ${!form.is_planned ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                aria-pressed={!form.is_planned}
                data-testid="litter-mode-actual"
              >
                Actual litter
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, is_planned: true })}
                className={`px-3 py-1.5 text-xs font-semibold rounded ${form.is_planned ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                aria-pressed={!!form.is_planned}
                data-testid="litter-mode-planned"
              >
                Planned litter
              </button>
            </div>
          </div>
          <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="litter-create-form">
            <div>
              <Label htmlFor="dam-dog">Dam (existing dog){(dogs || []).length > 0 ? " *" : ""}</Label>
              <select
                id="dam-dog"
                value={form.dam_dog_id}
                onChange={(e) => setForm({ ...form, dam_dog_id: e.target.value })}
                required={(dogs || []).length > 0}
                className="mt-1.5 h-9 w-full border border-border rounded-md bg-card px-3 text-sm"
                data-testid="litter-dam-dog"
              >
                <option value="">— Link a registered dog —</option>
                {(dogs || []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{d.breed ? ` · ${d.breed}` : ""}</option>
                ))}
              </select>
              {(dogs || []).length > 0 ? (
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">Linked so this litter appears on the dam&apos;s profile.</p>
              ) : (
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">Add a dog first to link this litter to a profile.</p>
              )}
            </div>
            <div>
              <Label htmlFor="dam-name">Dam name {(dogs || []).length > 0 ? "(optional override)" : "*"}</Label>
              <Input id="dam-name" value={form.dam_name} onChange={(e) => setForm({ ...form, dam_name: e.target.value })} className="mt-1.5" placeholder={(dogs || []).length > 0 ? "Leave blank to use the linked dog's name" : "Dam's name"} required={(dogs || []).length === 0} data-testid="litter-dam-name" />
            </div>
            <div>
              <Label htmlFor="stud-name">Stud</Label>
              <Input id="stud-name" value={form.stud_name} onChange={(e) => setForm({ ...form, stud_name: e.target.value })} className="mt-1.5" placeholder="Sire's name" data-testid="litter-stud-name" />
            </div>
            <div>
              <Label htmlFor="backup-stud-name">Back-up stud</Label>
              <Input id="backup-stud-name" value={form.backup_stud_name} onChange={(e) => setForm({ ...form, backup_stud_name: e.target.value })} className="mt-1.5" placeholder="Optional fallback sire" data-testid="litter-backup-stud-name" />
            </div>
            <div>
              <Label htmlFor="stud-fee">Stud fee</Label>
              <Input id="stud-fee" type="number" min="0" step="0.01" value={form.stud_fee} onChange={(e) => setForm({ ...form, stud_fee: e.target.value })} className="mt-1.5" placeholder="0.00" data-testid="litter-stud-fee" />
            </div>
            <div>
              <Label htmlFor="bred-on">{form.is_planned ? "Target breeding date" : "Breeding date"}</Label>
              <Input id="bred-on" type="date" value={form.bred_on} onChange={(e) => setForm({ ...form, bred_on: e.target.value })} className="mt-1.5" data-testid="litter-bred-on" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={creating} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="litter-create-submit">
                <Plus size={14} className="mr-1.5" /> {creating ? "Saving…" : (form.is_planned ? "Save planned litter" : "Create litter")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div data-testid="litters-list">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : litters.length === 0 ? (
          <Card className="border-dashed border-border shadow-none rounded-lg">
            <CardContent className="p-8 text-center">
              <Baby size={28} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No litters yet. Create one above to start tracking puppies, ENS, and socialization.</p>
            </CardContent>
          </Card>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {litters.map((l) => {
              const label = (l.dam_name || (dogs || []).find((d) => d.id === l.dam_dog_id)?.name || "Dam unknown") + (l.stud_name ? ` × ${l.stud_name}` : "");
              const due = l.timeline?.expected_whelp_day_63;
              const remaining = l.timeline?.days_remaining;
              return (
                <li key={l.id}>
                  <Card className="border-border shadow-none rounded-lg hover:-translate-y-0.5 transition-transform" data-testid={`litter-row-${l.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <Link to={`/breeder/litters/${l.id}`} className="block" data-testid={`litter-open-${l.id}`}>
                            <p className="font-medium truncate flex items-center gap-2" data-testid={`litter-label-${l.id}`}>
                              <span className="truncate">{label}</span>
                              {l.is_planned ? (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 text-[10px] uppercase tracking-wider font-semibold shrink-0"
                                  data-testid={`litter-planned-badge-${l.id}`}
                                  title="Planned litter — not yet whelped"
                                >
                                  <Sparkles size={9} /> Planned
                                </span>
                              ) : null}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {l.puppy_count} {l.puppy_count === 1 ? "puppy" : "puppies"}
                              {l.bred_on ? ` · ${l.is_planned ? "target" : "bred"} ${l.bred_on}` : ""}
                              {due ? ` · due ${due}${remaining >= 0 ? ` (in ${remaining}d)` : ""}` : ""}
                            </p>
                            {(l.backup_stud_name || l.stud_fee > 0) && (
                              <p className="text-xs text-muted-foreground mt-0.5" data-testid={`litter-stud-extras-${l.id}`}>
                                {l.backup_stud_name ? `Back-up stud: ${l.backup_stud_name}` : ""}
                                {l.backup_stud_name && l.stud_fee > 0 ? " · " : ""}
                                {l.stud_fee > 0 ? `Stud fee: $${Number(l.stud_fee).toFixed(2)}` : ""}
                              </p>
                            )}
                          </Link>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {l.is_planned && (
                            <button
                              onClick={() => promote(l.id, label)}
                              className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 flex items-center gap-1"
                              data-testid={`litter-promote-${l.id}`}
                              title="Promote this planned litter into an actual litter"
                            >
                              <Baby size={12} /> Promote
                            </button>
                          )}
                          <button onClick={() => remove(l.id, label)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive" data-testid={`litter-delete-${l.id}`} aria-label="Delete litter">
                            <Trash2 size={15} />
                          </button>
                          <Link to={`/breeder/litters/${l.id}`} className="p-2 hover:bg-muted rounded-md text-muted-foreground">
                            <ChevronRight size={16} />
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
